import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { ArrowLeft, Bell, Clock, Calendar, CheckCircle2, MessageSquare, Loader2, Send, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Phoenix", label: "Arizona (MST)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Anchorage", label: "Alaska Time (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HST)" },
];

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = [0, 15, 30, 45];

const DAYS = [
  { value: 1, label: 'Mon', full: 'Monday' },
  { value: 2, label: 'Tue', full: 'Tuesday' },
  { value: 3, label: 'Wed', full: 'Wednesday' },
  { value: 4, label: 'Thu', full: 'Thursday' },
  { value: 5, label: 'Fri', full: 'Friday' },
  { value: 6, label: 'Sat', full: 'Saturday' },
  { value: 7, label: 'Sun', full: 'Sunday' },
];

export default function PrimeDigestSettings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  
  const [settings, setSettings] = useState({
    enabled: true,
    time_hour: 8,
    time_minute: 0,
    timezone: "America/New_York",
    include_calendar: true,
    include_tasks: true,
    include_unread_messages: false,
    days_of_week: [1, 2, 3, 4, 5]
  });

  useEffect(() => {
    fetchSettings();
  }, [user]);

  const fetchSettings = async () => {
    if (!user?.id) return;
    try {
      const response = await axios.get(`${API}/api/prime/digest-preferences/${user.id}`);
      if (response.data?.digest_settings) {
        setSettings(response.data.digest_settings);
      }
    } catch (error) {
      console.error('Failed to fetch digest settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!user?.id) return;
    setSaving(true);
    try {
      await axios.put(`${API}/api/prime/digest-preferences/${user.id}`, settings);
      toast.success('Digest preferences saved!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const sendTestDigest = async () => {
    if (!user?.id) return;
    setSending(true);
    try {
      const response = await axios.post(`${API}/api/prime/task-digest/send/${user.id}`);
      if (response.data?.sent) {
        toast.success('Test digest sent! Check your Prime DM.');
      } else {
        toast.info('No digest sent - you have no tasks or events.');
      }
    } catch (error) {
      console.error('Failed to send test digest:', error);
      toast.error('Failed to send test digest');
    } finally {
      setSending(false);
    }
  };

  const previewDigest = async () => {
    if (!user?.id) return;
    setPreviewing(true);
    try {
      const response = await axios.get(`${API}/api/prime/task-digest/preview/${user.id}`);
      setPreviewContent(response.data?.preview || 'No content to preview');
    } catch (error) {
      console.error('Failed to preview digest:', error);
      toast.error('Failed to preview digest');
    } finally {
      setPreviewing(false);
    }
  };

  const toggleDay = (day) => {
    setSettings(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(day)
        ? prev.days_of_week.filter(d => d !== day)
        : [...prev.days_of_week, day].sort((a, b) => a - b)
    }));
  };

  // Convert 24-hour to 12-hour format
  const get12Hour = (hour24) => hour24 % 12 || 12;
  const getAmPm = (hour24) => hour24 < 12 ? 'AM' : 'PM';

  // Convert 12-hour to 24-hour format
  const to24Hour = (hour12, amPm) => {
    if (amPm === 'AM') {
      return hour12 === 12 ? 0 : hour12;
    } else {
      return hour12 === 12 ? 12 : hour12 + 12;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="prime-digest-settings">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/settings')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              🔱 Prime Daily Digest
            </h1>
            <p className="text-muted-foreground">
              Configure when and how you receive your morning briefing
            </p>
          </div>
        </div>

        {/* Enable/Disable */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-purple-500/10">
                  <Bell className="h-6 w-6 text-purple-500" />
                </div>
                <div>
                  <h3 className="font-semibold">Daily Digest</h3>
                  <p className="text-sm text-muted-foreground">
                    Receive a morning summary of your schedule and tasks
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
                data-testid="digest-enabled-switch"
              />
            </div>
          </CardContent>
        </Card>

        {settings.enabled && (
          <>
            {/* Time Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Delivery Time
                </CardTitle>
                <CardDescription>
                  Choose when you want to receive your daily digest
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex flex-wrap gap-4 items-end">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Hour</label>
                    <Select 
                      value={get12Hour(settings.time_hour).toString()} 
                      onValueChange={(v) => {
                        const hour12 = parseInt(v);
                        const amPm = getAmPm(settings.time_hour);
                        setSettings({ ...settings, time_hour: to24Hour(hour12, amPm) });
                      }}
                    >
                      <SelectTrigger className="w-[80px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {HOURS.map(h => (
                          <SelectItem key={h} value={h.toString()}>{h}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium mb-2 block">Minute</label>
                    <Select 
                      value={settings.time_minute.toString()} 
                      onValueChange={(v) => setSettings({ ...settings, time_minute: parseInt(v) })}
                    >
                      <SelectTrigger className="w-[80px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MINUTES.map(m => (
                          <SelectItem key={m} value={m.toString()}>{m.toString().padStart(2, '0')}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium mb-2 block">AM/PM</label>
                    <Select 
                      value={getAmPm(settings.time_hour)} 
                      onValueChange={(v) => {
                        const hour12 = get12Hour(settings.time_hour);
                        setSettings({ ...settings, time_hour: to24Hour(hour12, v) });
                      }}
                    >
                      <SelectTrigger className="w-[80px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="AM">AM</SelectItem>
                        <SelectItem value="PM">PM</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex-1 min-w-[180px]">
                    <label className="text-sm font-medium mb-2 block">Timezone</label>
                    <Select 
                      value={settings.timezone} 
                      onValueChange={(v) => setSettings({ ...settings, timezone: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TIMEZONES.map(tz => (
                          <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Separator />

                <div>
                  <label className="text-sm font-medium mb-3 block">Days of the Week</label>
                  <div className="flex flex-wrap gap-2">
                    {DAYS.map(day => (
                      <Button
                        key={day.value}
                        variant={settings.days_of_week.includes(day.value) ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => toggleDay(day.value)}
                        className="w-12"
                        title={day.full}
                      >
                        {day.label}
                      </Button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Select which days you want to receive the digest
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Content Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5" />
                  Content Preferences
                </CardTitle>
                <CardDescription>
                  Choose what to include in your daily digest
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-blue-500" />
                    <div>
                      <p className="font-medium">Calendar Events</p>
                      <p className="text-sm text-muted-foreground">Today's meetings and appointments</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.include_calendar}
                    onCheckedChange={(checked) => setSettings({ ...settings, include_calendar: checked })}
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    <div>
                      <p className="font-medium">Tasks</p>
                      <p className="text-sm text-muted-foreground">Pending, overdue, and high priority tasks</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.include_tasks}
                    onCheckedChange={(checked) => setSettings({ ...settings, include_tasks: checked })}
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="h-5 w-5 text-orange-500" />
                    <div>
                      <p className="font-medium">Unread Messages</p>
                      <p className="text-sm text-muted-foreground">Summary of unread DMs and mentions</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.include_unread_messages}
                    onCheckedChange={(checked) => setSettings({ ...settings, include_unread_messages: checked })}
                  />
                  <Badge variant="outline" className="ml-2">Coming Soon</Badge>
                </div>
              </CardContent>
            </Card>

            {/* Preview Section */}
            {previewContent && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="h-5 w-5" />
                    Digest Preview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-muted/50 rounded-lg p-4 whitespace-pre-wrap text-sm font-mono">
                    {previewContent}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          <Button onClick={saveSettings} disabled={saving} data-testid="save-digest-settings">
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Preferences'
            )}
          </Button>
          
          {settings.enabled && (
            <>
              <Button variant="outline" onClick={previewDigest} disabled={previewing}>
                {previewing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4 mr-2" />
                    Preview Digest
                  </>
                )}
              </Button>
              
              <Button variant="outline" onClick={sendTestDigest} disabled={sending}>
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Test Now
                  </>
                )}
              </Button>
            </>
          )}
        </div>

        {/* Info Box */}
        <Card className="bg-muted/50 border-dashed">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">
              💡 <strong>Tip:</strong> Your daily digest will be delivered as a DM from Prime. 
              You can find it in <strong>Chat → Direct Messages → 🔱 Prime</strong>.
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
